import type { SalesTransactionDiscountType } from '@/api/models';

/**
 * The arithmetic of a discounted cart (SEL-100), the sale point's copy of
 * bos-api's rule (`SaleDiscountAllocator`, API-159) so the preview matches
 * what the server charges to the tetri.
 *
 * The cart's money stays float GEL as the catalog delivers it; these helpers
 * alone convert each price to integer tetri on entry, work in integers and
 * return tetri. A percentage rounds half away from zero (every amount here is
 * positive, so half up).
 *
 * Order: a line's list total is unit price × quantity. Its discount is the
 * fixed value, or the list total × percentage ÷ 100, taken on the list total
 * and not per unit (3 × 0.05 at 33% is 0.05, not 3 × 0.02). The sale discount
 * then applies to what the lines leave. A fixed line discount belongs to the
 * line and keeps its amount when the quantity changes; a percentage follows
 * the quantity.
 *
 * Fit: a line may be discounted to exactly 0.00 but not below; the sale total
 * must stay above 0.00 once any discount is set. A discount that does not fit
 * is refused, never trimmed: the figures are still computed from the value as
 * entered, and `fit` names the first problem in the server's order (lines in
 * cart order, then the sale discount, then the total).
 */

/** A discount as the seller entered it. */
export type Discount = {
    type: SalesTransactionDiscountType;
    /** A percentage (above 0, up to 100, two decimals) or GEL (whole tetri, above 0). */
    value: number;
    /** Trimmed; empty means none. */
    note: string;
};

/** The largest percentage a discount may carry. */
export const MAX_DISCOUNT_PERCENTAGE = 100;

/** The longest note a discount may carry, after trimming. */
export const DISCOUNT_NOTE_MAX_LENGTH = 200;

/**
 * Why a discount cannot be used: `valueInvalid`, a value of the wrong shape;
 * `lineTooLarge`, a line discount above the line's list total;
 * `totalNotPositive`, discounts that leave the sale at or below 0.00.
 */
export type DiscountProblemKind =
    'valueInvalid' | 'lineTooLarge' | 'totalNotPositive';

/** `fits`, or the problem that stops the discount being used. */
export type DiscountFit = 'fits' | DiscountProblemKind;

/** GEL as the catalog delivers it → whole tetri. */
export const toTetri = (gel: number): number => Math.round(gel * 100);

/** Whole tetri → GEL. */
export const toGel = (tetri: number): number => tetri / 100;

/** Whether `value` has at most two decimals, allowing for float noise. */
const hasAtMostTwoDecimals = (value: number): boolean =>
    Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;

/** Whether the value is one bos-api accepts for the discount's type. */
export const isDiscountValueValid = (discount: Discount): boolean =>
    Number.isFinite(discount.value) &&
    discount.value > 0 &&
    hasAtMostTwoDecimals(discount.value) &&
    (discount.type === 'fixed' || discount.value <= MAX_DISCOUNT_PERCENTAGE);

/**
 * What the discount takes off `baseTetri` (a line's list total, or what the
 * lines leave for the sale discount). Not capped: whether it fits is the
 * caller's question. An invalid value takes nothing.
 */
export const discountAmountTetri = (
    discount: Discount,
    baseTetri: number,
): number => {
    if (!isDiscountValueValid(discount)) {
        return 0;
    }
    if (discount.type === 'fixed') {
        return toTetri(discount.value);
    }
    // base × (percentage in hundredths) ÷ 10 000, half up, in integers.
    return Math.floor((baseTetri * toTetri(discount.value) + 5_000) / 10_000);
};

/** One cart line's figures in tetri. */
export type PricedLine = {
    listTotalTetri: number;
    discountTetri: number;
    /** List total − its own discount; negative when the discount does not fit. */
    afterDiscountTetri: number;
    fit: DiscountFit;
};

/** One line to price: the catalog's unit price in GEL, the units and its discount. */
export type LineToPrice = {
    unitPrice: number;
    quantity: number;
    discount: Discount | null;
};

export const priceLine = ({
    unitPrice,
    quantity,
    discount,
}: LineToPrice): PricedLine => {
    const listTotalTetri = toTetri(unitPrice) * quantity;
    if (discount === null) {
        return {
            listTotalTetri,
            discountTetri: 0,
            afterDiscountTetri: listTotalTetri,
            fit: 'fits',
        };
    }
    const discountTetri = discountAmountTetri(discount, listTotalTetri);
    const fit: DiscountFit = !isDiscountValueValid(discount)
        ? 'valueInvalid'
        : discountTetri > listTotalTetri
          ? 'lineTooLarge'
          : 'fits';
    return {
        listTotalTetri,
        discountTetri,
        afterDiscountTetri: listTotalTetri - discountTetri,
        fit,
    };
};

/** The whole cart's figures in tetri. */
export type PricedSale = {
    lines: PricedLine[];
    /** Sum of list totals. */
    subtotalTetri: number;
    lineDiscountTotalTetri: number;
    /** Subtotal − line discounts: what the sale discount applies to. */
    afterLinesTetri: number;
    saleDiscountTetri: number;
    /** Line discounts + the sale discount. */
    discountTotalTetri: number;
    /** What is charged: after-lines − the sale discount. */
    totalTetri: number;
    /** The first problem, in the server's order. */
    fit: DiscountFit;
    /** The index of the line `fit` is about; `null` when it is about the sale. */
    problemLineIndex: number | null;
};

export const priceSale = (
    lines: LineToPrice[],
    saleDiscount: Discount | null,
): PricedSale => {
    const priced = lines.map(priceLine);
    const subtotalTetri = priced.reduce((sum, l) => sum + l.listTotalTetri, 0);
    const lineDiscountTotalTetri = priced.reduce(
        (sum, l) => sum + l.discountTetri,
        0,
    );
    const afterLinesTetri = subtotalTetri - lineDiscountTotalTetri;
    const saleDiscountTetri =
        saleDiscount === null
            ? 0
            : discountAmountTetri(saleDiscount, afterLinesTetri);
    const totalTetri = afterLinesTetri - saleDiscountTetri;

    const lineProblem = priced.findIndex((l) => l.fit !== 'fits');
    const anyDiscount =
        saleDiscount !== null || lines.some((l) => l.discount !== null);

    let fit: DiscountFit = 'fits';
    let problemLineIndex: number | null = null;
    if (lineProblem !== -1) {
        fit = priced[lineProblem]!.fit;
        problemLineIndex = lineProblem;
    } else if (saleDiscount !== null && !isDiscountValueValid(saleDiscount)) {
        fit = 'valueInvalid';
    } else if (anyDiscount && totalTetri <= 0) {
        fit = 'totalNotPositive';
    }

    return {
        lines: priced,
        subtotalTetri,
        lineDiscountTotalTetri,
        afterLinesTetri,
        saleDiscountTetri,
        discountTotalTetri: lineDiscountTotalTetri + saleDiscountTetri,
        totalTetri,
        fit,
        problemLineIndex,
    };
};
